import React, { useEffect, useState } from 'react';
import { Download, Smartphone, X, Share } from 'lucide-react';

const DISMISS_KEY = 'domingao_pwa_install_dismissed';

/**
 * Install-to-home-screen prompt:
 *   - Android/desktop Chrome: catches beforeinstallprompt and shows "Instalar App" button.
 *   - iOS Safari: no native prompt exists, so we show a small "Adicionar à tela inicial" tip.
 *   - Hidden when already running in standalone mode.
 */
const PWAInstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSTip, setShowIOSTip] = useState(false);

  useEffect(() => {
    const dismissed = (() => { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; } })();
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (standalone || dismissed) return;

    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    if (iOS) {
      setVisible(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIOSTip(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  const install = async () => {
    if (isIOS) { setShowIOSTip(true); return; }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') dismiss();
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div className="mb-4" data-testid="pwa-install-banner">
      <div className="relative rounded-xl bg-gradient-to-r from-slate-900 to-emerald-900 text-white shadow-lg border border-emerald-500/30 overflow-hidden">
        <div className="flex items-center gap-3 p-4 pr-12">
          <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 border border-emerald-400/40">
            <Smartphone size={22} className="text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">📱 Instalar como App</p>
            <p className="text-sm sm:text-base font-black leading-tight">Adiciona o Domingão ao teu ecrã inicial</p>
            <p className="text-xs opacity-80 hidden sm:block">Acesso rápido, sem barra de navegador, funciona offline.</p>
          </div>
          <button
            onClick={install}
            className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black px-4 py-2 rounded-lg text-sm shrink-0 transition-colors"
            data-testid="pwa-install-btn"
          >
            <Download size={16} /> {isIOS ? 'Como instalar' : 'Instalar'}
          </button>
        </div>
        <button onClick={install} className="sm:hidden mx-4 mb-3 w-[calc(100%-2rem)] bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black py-2 rounded-lg text-sm flex items-center justify-center gap-1.5" data-testid="pwa-install-btn-mobile">
          <Download size={16} /> {isIOS ? 'Como instalar' : 'Instalar App'}
        </button>
        <button
          onClick={dismiss}
          aria-label="Fechar"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 text-white flex items-center justify-center"
          data-testid="pwa-dismiss-btn"
        >
          <X size={14} strokeWidth={3} />
        </button>
      </div>

      {showIOSTip && (
        <div className="mt-2 rounded-lg bg-slate-100 border border-slate-300 p-3 text-sm text-slate-700 flex items-start gap-2" data-testid="pwa-ios-tip">
          <Share className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            No Safari, toca em <b>Partilhar</b> (o ícone de caixa com seta) e depois em <b>“Adicionar ao Ecrã Principal”</b>.
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstallBanner;
